import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from "react";
import Link from "next/link";
import { cx } from "./cx";
import { Spinner } from "./spinner";
import { Tooltip, type TooltipPosition } from "./tooltip";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "upgrade" | "destructive" | "inverse";
export type ButtonSize = "sm" | "md" | "lg" | "xl";
/** Actions use the standard rounded rectangle; pills are reserved for non-action controls. */
export type ButtonShape = "rect";

/** Single source of truth for button class strings. Used by Button, ButtonLink, IconButton. */
export function buttonClasses(
  opts: { variant?: ButtonVariant; size?: ButtonSize; fullWidth?: boolean; shape?: ButtonShape } = {},
  className?: string,
): string {
  const { variant = "primary", size = "md", fullWidth = false, shape = "rect" } = opts;
  return cx(
    "pz-btn",
    `pz-btn--${variant}`,
    size !== "md" && `pz-btn--${size}`,
    fullWidth && "pz-btn--block",
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretches the button to fill its container (card/form footers). */
  fullWidth?: boolean;
  /** Retained for explicit call sites; actions always use the standard rectangle. */
  shape?: ButtonShape;
  /** Replaces the leading icon with a spinner and blocks interaction. */
  loading?: boolean;
  iconLeading?: ReactNode;
  iconTrailing?: ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  shape = "rect",
  loading = false,
  iconLeading,
  iconTrailing,
  className,
  children,
  disabled,
  type = "button",
  ...rest
}: ButtonProps) {
  // `inverse` is a light pill (accent label on white), so its spinner takes the
  // muted tone like the other light variants — only the dark fills invert.
  const spinnerTone = variant === "primary" || variant === "destructive" ? "inverse" : "muted";
  return (
    <button
      type={type}
      className={buttonClasses({ variant, size, fullWidth, shape }, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size="sm" tone={spinnerTone} /> : iconLeading}
      {children}
      {iconTrailing}
    </button>
  );
}

/**
 * The anchor twin of `Button` — a Next `<Link>` that carries the IDENTICAL
 * `.pz-btn*` classes (via `buttonClasses`). Navigational CTAs get the real
 * button silhouette without hand-rolling per-page CSS, which is what let anchor
 * and button buttons drift apart (the invisible-hover bug this consolidates).
 */
export interface ButtonLinkProps
  extends Omit<ComponentProps<typeof Link>, "className">,
    Pick<ButtonProps, "variant" | "size" | "fullWidth" | "shape" | "iconLeading" | "iconTrailing"> {
  className?: string;
}

export function ButtonLink({
  variant,
  size,
  fullWidth,
  shape,
  iconLeading,
  iconTrailing,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link className={buttonClasses({ variant, size, fullWidth, shape }, className)} {...rest}>
      {iconLeading}
      {children}
      {iconTrailing}
    </Link>
  );
}

/** Icon-only button (map-chrome style: white circle, ink icon). Requires a label. */
export function IconButton({
  label,
  tooltip,
  className,
  variant = "secondary",
  children,
  ...rest
}: Omit<ButtonProps, "iconLeading" | "iconTrailing"> & {
  label: string;
  /** Show the label as a custom hover/focus tooltip (never the native title=). */
  tooltip?: TooltipPosition;
}) {
  const button = (
    <Button variant={variant} className={cx("pz-btn--icon", className)} aria-label={label} {...rest}>
      {children}
    </Button>
  );
  // The tooltip mirrors the aria-label, so the visible hint and the a11y name
  // never drift. Opt-in per call site (icon-only controls that benefit from it).
  return tooltip ? (
    <Tooltip content={label} position={tooltip}>
      {button}
    </Tooltip>
  ) : (
    button
  );
}
